/***
 * Componente based on the NgxMatIntlTelInput
 *
 * lot of update and improvments but the initial idea came from this
 *
 * https://github.com/tanansatpal/ngx-mat-intl-tel-input
 */
import { MatFormFieldControl } from '@angular/material/form-field'
import {
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  AfterViewInit,
} from '@angular/core'

import { FormGroupDirective, NG_VALIDATORS, NgControl, NgForm } from '@angular/forms'
import { Country } from './model/country.model'
import { PhoneNumberFormat } from './model/phone-number-format.model'
import {
  AsYouType,
  CountryCode as CC,
  parsePhoneNumberFromString,
  parsePhoneNumberWithError,
  PhoneNumber,
} from 'libphonenumber-js'

import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { Subject } from 'rxjs'
import { FocusMonitor } from '@angular/cdk/a11y'
import { CanUpdateErrorState, ErrorStateMatcher, mixinErrorState } from '@angular/material/core'
import { MatMenu } from '@angular/material/menu'
import { GbCustomValidators } from 'libs/shared/utils/helpers/src/lib/form-validators/custom-form.validator'
import { PhoneNumberUtil } from 'google-libphonenumber'
import { MatInput } from '@angular/material/input'

/** @docs-private */
export type Constructor<T> = new (...args: any[]) => T

/**
 * This is a permissive type for abstract class constructors.
 * @docs-private
 */
export type AbstractConstructor<T = object> = abstract new (...args: any[]) => T

const defaultSelectedCountry = {
  name: '',
  iso2: 'UN',
  dialCode: '',
  flagClass: 'UN',
}

class GbIntlPhoneInputBase {
  constructor(
    public _parentFormGroup: FormGroupDirective,
    public _parentForm: NgForm,
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    public ngControl: NgControl,
    public stateChanges: Subject<void>,
  ) {}
}

declare type CanUpdateErrorStateCtor = Constructor<CanUpdateErrorState> & AbstractConstructor<CanUpdateErrorState>

const _GbIntlPhoneInputMixinBase: CanUpdateErrorStateCtor & typeof GbIntlPhoneInputBase =
  mixinErrorState(GbIntlPhoneInputBase)

@Component({
  selector: 'gb-intl-phone-input',
  templateUrl: './gb-intl-phone-input.component.html',
  styleUrls: ['./gb-intl-phone-input.component.scss'],
  providers: [
    { provide: MatFormFieldControl, useExisting: GbIntlPhoneInputComponent },
    {
      provide: NG_VALIDATORS,
      useValue: GbCustomValidators.mobileNumberValidator(),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbIntlPhoneInputComponent
  extends _GbIntlPhoneInputMixinBase
  implements OnInit, OnDestroy, AfterViewInit, DoCheck, CanUpdateErrorState, MatFormFieldControl<any>
{
  static nextId = 0

  preferredCountries: Array<string> = ['US', 'CA', 'GB', 'AU', 'NZ', 'IE']
  @Input() name!: string
  @Input() onlyCountries: Array<string> = []
  @Input() auctionCountryCode?: string | null
  @Input() errorStateMatcher!: ErrorStateMatcher
  @Input() searchPlaceholder!: string
  @Input() describedBy = ''
  @Input() allowNonNumeric = false
  @Input() focusInput = false

  @Input()
  get format(): PhoneNumberFormat {
    return this._format
  }

  set format(value: PhoneNumberFormat) {
    this._format = value
    this.phoneNumber = this.formattedPhoneNumber
    this.stateChanges.next()
  }

  @ViewChild(MatMenu) matMenu!: MatMenu
  @ViewChild(MatInput) matInput!: MatInput
  private _placeholder!: string
  private _required = false
  private _disabled = false
  stateChanges = new Subject<void>()
  focused = false
  @HostBinding()
  id = `gb-intl-phone-input-${GbIntlPhoneInputComponent.nextId++}`
  phoneNumber = ''
  allCountries: Array<Country> = []
  allCountriesInDropDown: Array<Country> = []
  preferredCountriesInDropDown: Array<Country> = []
  selectedCountry: Country = defaultSelectedCountry
  numberInstance: PhoneNumber | undefined
  value!: string | undefined
  searchCriteria!: string
  @Output()
  countryChanged: EventEmitter<Country> = new EventEmitter<Country>()

  private previousFormattedNumber!: string
  private _format: PhoneNumberFormat = 'default'

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  propagateChange = (_: any) => {}

  constructor(
    private fm: FocusMonitor,
    private _changeDetectorRef: ChangeDetectorRef,
    private elRef: ElementRef<HTMLElement>,
    @Optional() @Self() public ngControl: NgControl,
    @Optional() _parentForm: NgForm,
    @Optional() _parentFormGroup: FormGroupDirective,
    @Optional() stateChanges: Subject<void>,
    _defaultErrorStateMatcher: ErrorStateMatcher,
  ) {
    super(_parentFormGroup, _parentForm, _defaultErrorStateMatcher, ngControl, stateChanges)

    fm.monitor(elRef, true).subscribe((origin) => {
      if (this.focused && !origin) {
        this.onTouched()
      }
      this.focused = !!origin
      this.stateChanges.next()
    })
    this.setCountryData()
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this
    }
  }

  ngOnInit() {
    if (!this.searchPlaceholder) {
      this.searchPlaceholder = 'Search ...'
    }
    this.allCountriesInDropDown = this.allCountries
    // set the auctionCountry as one of the prefered international code.
    if (this.auctionCountryCode) {
      const auctionCountryCode = this.auctionCountryCode
      if (this.preferredCountries.includes(auctionCountryCode)) {
        this.preferredCountries = this.preferredCountries.filter((iso2) => iso2 !== auctionCountryCode)
      }
      this.preferredCountries = [auctionCountryCode].concat(this.preferredCountries)
    }
    // set preferred countries and remove them from all country so
    // they won't show as duplicated
    if (this.preferredCountries.length) {
      this.preferredCountries.forEach((iso2) => {
        const preferredCountry = this.allCountries.filter((c) => c.iso2 === iso2).shift()
        if (preferredCountry) this.preferredCountriesInDropDown.push(preferredCountry)
      })

      this.allCountriesInDropDown = this.allCountries.filter((c) => !this.preferredCountries.includes(c.iso2))
    }

    // Remove countries from dropdown not included in onlyCountries if input passed
    if (this.onlyCountries.length) {
      this.allCountriesInDropDown = this.allCountries.filter((c) => this.onlyCountries.includes(c.iso2))
    }

    if (this.numberInstance && this.numberInstance.country) {
      // If an existing number is present, we use it to determine selectedCountry
      const country = this.getCountry(this.numberInstance.country)
      if (country.dialCode) {
        this.selectedCountry = country
      }
    } else {
      this.selectedCountry = this.preferredCountriesInDropDown[0]
    }
    this.countryChanged.emit(this.selectedCountry)
    this._changeDetectorRef.markForCheck()
    this.stateChanges.next()
    this.onPhoneNumberChange()
  }

  ngAfterViewInit(): void {
    if (this.focusInput) {
      this.matInput.focus()
    }
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.updateErrorState()
    }
  }

  public onPhoneNumberChange(): void {
    try {
      if (this.allowNonNumeric && isNaN(+this.phoneNumber)) {
        throw new Error('not a number')
      }

      this.numberInstance = parsePhoneNumberWithError(this.phoneNumber.toString(), this.selectedCountry?.iso2 as CC)

      this.formatAsYouTypeIfEnabled()
      this.value = this.numberInstance?.number

      if (this.numberInstance && this.numberInstance.isValid()) {
        if (this.phoneNumber !== this.formattedPhoneNumber) {
          this.phoneNumber = this.formattedPhoneNumber
        }
        if (this.numberInstance.country && this.selectedCountry?.iso2 !== this.numberInstance.country) {
          const country2 = this.getCountry(this.numberInstance.country)
          if (country2.dialCode) {
            this.selectedCountry = country2
          }
          this.countryChanged.emit(this.selectedCountry)
        }
      }
    } catch (e) {
      // if no possible numbers are there,
      // then the full number is passed so that validator could be triggered and proper error could be shown
      this.value = this.phoneNumber.toString()
    }

    this.propagateChange(this.value)
    // this._changeDetectorRef.markForCheck()
  }

  public onCountrySelect(country: Country): void {
    if (this.phoneNumber && this.numberInstance && this.numberInstance.nationalNumber) {
      this.phoneNumber = this.numberInstance.nationalNumber
    }
    this.selectedCountry = country

    this.countryChanged.emit(this.selectedCountry)
    this.onPhoneNumberChange()
  }

  public getCountry(code: string): Country {
    return this.allCountries.find((c) => c.iso2 === code) || defaultSelectedCountry
  }

  public onInputKeyPress(event: any): void {
    if (!this.allowNonNumeric) {
      const pattern = /[0-9+\- ]/
      if (!pattern.test(event.key)) {
        event.preventDefault()
      }
    }
  }

  protected setCountryData(): void {
    const phoneUtil = PhoneNumberUtil.getInstance()
    const regions = phoneUtil.getSupportedRegions()

    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })

    regions.forEach((region) => {
      const country: Country = {
        name: regionNames.of(region),
        iso2: region,
        dialCode: phoneUtil.getCountryCodeForRegion(region).toString(),
        flagClass: region,
      }

      this.allCountries.push(country)
    })
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
    this._changeDetectorRef.markForCheck()
    this.stateChanges.next()
  }

  writeValue(value: any): void {
    if (value) {
      this.numberInstance = parsePhoneNumberFromString(value)
      if (this.numberInstance) {
        const countryCode = this.numberInstance.country
        this.phoneNumber = this.formattedPhoneNumber
        if (!countryCode) {
          return
        }
        setTimeout(() => {
          const country3 = this.getCountry(countryCode)
          if (country3.dialCode) {
            this.selectedCountry = country3
          }

          if (this.selectedCountry.dialCode && !this.preferredCountries.includes(this.selectedCountry.iso2)) {
            this.preferredCountriesInDropDown.push(this.selectedCountry)
          }
          this.countryChanged.emit(this.selectedCountry)

          // Initial value is set
          this._changeDetectorRef.markForCheck()
          this.stateChanges.next()
        }, 1)
      } else {
        this.phoneNumber = value
      }
    } else {
      this.phoneNumber = ''
      this.numberInstance = undefined
    }

    // Value is set from outside using setValue()
    this._changeDetectorRef.markForCheck()
    this.stateChanges.next()
  }

  get empty(): boolean {
    return !this.phoneNumber
  }

  @HostBinding('class.ngx-floating')
  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty
  }

  @Input()
  get placeholder(): string {
    return this._placeholder
  }

  set placeholder(value: string) {
    this._placeholder = value
    this.stateChanges.next()
  }

  @Input()
  get required(): boolean {
    return this._required
  }

  set required(value: boolean) {
    this._required = coerceBooleanProperty(value)
    this.stateChanges.next()
  }

  @Input()
  get disabled(): boolean {
    return this._disabled
  }

  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value)
    this.stateChanges.next()
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ')
  }

  onContainerClick(event: MouseEvent): void {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.elRef.nativeElement.querySelector('input')!.focus()
    }
  }

  reset(): void {
    this.phoneNumber = ''
    this.propagateChange(null)

    this._changeDetectorRef.markForCheck()
    this.stateChanges.next()
  }

  ngOnDestroy(): void {
    this.stateChanges.complete()
    this.fm.stopMonitoring(this.elRef)
  }

  private get formattedPhoneNumber(): string {
    if (!this.numberInstance) {
      return this.phoneNumber.toString()
    }
    switch (this.format) {
      case 'national':
        return this.numberInstance.formatNational()
      case 'international':
        return this.numberInstance.formatInternational()
      default:
        return this.numberInstance.nationalNumber.toString()
    }
  }

  private formatAsYouTypeIfEnabled(): void {
    if (this.format === 'default') {
      return
    }
    const asYouType: AsYouType = new AsYouType(this.selectedCountry.iso2.toUpperCase() as CC)
    // To avoid caret positioning we apply formatting only if the caret is at the end:
    if (this.phoneNumber.toString().startsWith(this.previousFormattedNumber || '')) {
      this.phoneNumber = asYouType.input(this.phoneNumber.toString())
    }
    this.previousFormattedNumber = this.phoneNumber.toString()
  }
}
